import type { FundDetail } from "@/data/fund-detail-types";
import type { PdDataPoint } from "@/hooks/use-etf-live-nav";
import { cn } from "@/lib/utils";
/**
 * Buffer-fund Performance + Premium/Discount placeholder sections. Ported
 * from the dane etf-website `components/buffer-perf-placeholder.tsx`.
 */
import { useCallback, useRef, useState } from "react";
import type { PremiumDiscountQuarterlyRow } from "./leveraged-premium-discount";

const PERF_COLUMNS = [
  "1-Month",
  "3-Month",
  "YTD",
  "1-Year",
  "3-Year",
  "5-Year",
  "Since Inception (Cum.)",
] as const;

const PERF_ROW_LABELS = ["NAV", "Market Price"] as const;

export function BufferPerformancePlaceholder({ fund: _fund }: { fund: FundDetail }) {
  const [view, setView] = useState<"month" | "quarter">("month");

  const cellClass =
    "font-[family-name:var(--font-dmmono)] text-[13px] text-[var(--etf-white3)] py-3.5 px-3 text-right border-b border-[var(--etf-border2)]";
  const headerCellClass =
    "font-[family-name:var(--font-geist)] text-[11px] tracking-[0.1em] uppercase text-[var(--etf-white3)] py-3 px-3 text-right border-b border-[var(--etf-border2)] font-medium";
  const rowLabelClass =
    "font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white2)] tracking-[0.04em] py-3.5 px-3 text-left border-b border-[var(--etf-border2)] font-medium";
  const toggleBtnClass = (active: boolean) =>
    cn(
      "font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.06em] px-3 py-1.5 rounded-sm transition-colors duration-150 cursor-pointer bg-transparent border border-[var(--etf-border2)]",
      active
        ? "text-[var(--etf-white)] border-[var(--etf-orange)] bg-[rgba(255,85,0,0.08)]"
        : "text-[var(--etf-white3)] hover:text-[var(--etf-white2)]",
    );

  return (
    <section
      className="border-b border-[var(--etf-border)]"
      id="sec-performance"
      data-fund-section="showPerformanceChart"
    >
      <div className="flex items-center justify-between px-10 max-md:px-4 py-6 border-b border-[var(--etf-border)]">
        <div>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Performance
          </h2>
          <p className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em] mt-1 pl-[15px]">
            NAV vs Market Price &middot; Since Inception
          </p>
        </div>
      </div>
      <div className="px-10 max-md:px-4 pt-5 pb-3">
        <div className="flex items-center justify-between gap-3 mb-3 max-md:flex-col max-md:items-start">
          <div className="font-[family-name:var(--font-instrument)] text-[13px] tracking-[0.04em] text-[var(--etf-white)] font-medium">
            Performance History
          </div>
          <div className="flex gap-2" role="tablist" aria-label="Performance period">
            <button
              type="button"
              role="tab"
              aria-selected={view === "month"}
              className={toggleBtnClass(view === "month")}
              onClick={() => setView("month")}
            >
              Month-End
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "quarter"}
              className={toggleBtnClass(view === "quarter")}
              onClick={() => setView("quarter")}
            >
              Quarter-End
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[640px]">
            <thead>
              <tr>
                <th scope="col" className="py-3 px-3 border-b border-[var(--etf-border2)]" />
                {PERF_COLUMNS.map((col) => (
                  <th key={col} scope="col" className={headerCellClass}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERF_ROW_LABELS.map((label) => (
                <tr key={label}>
                  <th scope="row" className={rowLabelClass}>
                    {label}
                  </th>
                  {PERF_COLUMNS.map((col, ci) => (
                    <td key={col} className={cellClass}>
                      {ci === PERF_COLUMNS.length - 1 ? "—" : "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="px-10 max-md:px-4 py-5 border-t border-[var(--etf-border2)] font-[family-name:var(--font-geist)] text-[12.5px] text-[var(--etf-white)] italic leading-[1.55] m-0">
        The performance data quoted represents past performance. Past performance does not guarantee
        future results. Current performance may be lower or higher than the performance data quoted.
        The investment return and principal value of an investment will fluctuate so that an
        investor&rsquo;s shares, when sold or redeemed, may be worth more or less than their
        original cost. Returns less than one year are not annualized.
      </p>
    </section>
  );
}

function buildBufferPdFillPath(points: string): string {
  const pairs = points.trim().split(/\s+/);
  if (pairs.length === 0) return "";
  const firstX = pairs[0]?.split(",")[0];
  const lastX = pairs[pairs.length - 1]?.split(",")[0];
  return `M${firstX},80 L${pairs.join(" L")} L${lastX},80 Z`;
}

function bufferPdLastPoint(points: string): { cx: string; cy: string } {
  const pairs = points.trim().split(/\s+/);
  const last = pairs[pairs.length - 1];
  if (!last) return { cx: "0", cy: "80" };
  const [cx, cy] = last.split(",");
  return { cx: cx ?? "0", cy: cy ?? "80" };
}

const BUFFER_PD_DATE_LABEL_X = [20, 207, 393, 593, 755];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtBufferPdDate(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function parseBufferPdPoints(points: string): { x: number; y: number }[] {
  return points
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(",");
      return { x: Number.parseFloat(x!), y: Number.parseFloat(y!) };
    });
}

export function BufferPremiumDiscountPlaceholder({
  fund: _fund,
  chartPoints,
  dateLabels,
  quarterlyData,
  dataPoints,
}: {
  fund: FundDetail;
  chartPoints?: string;
  dateLabels?: string[];
  quarterlyData?: PremiumDiscountQuarterlyRow[];
  dataPoints?: PdDataPoint[];
}) {
  const hasRealData = chartPoints !== undefined && chartPoints.length > 0;
  const points = chartPoints ?? "";
  const labels = dateLabels ?? [];
  const fillPath = hasRealData ? buildBufferPdFillPath(points) : "";
  const endDot = hasRealData ? bufferPdLastPoint(points) : { cx: "0", cy: "0" };

  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const parsedPoints = hasRealData ? parseBufferPdPoints(points) : [];

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!dataPoints || dataPoints.length === 0 || !svgRef.current) return;
      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * 800;
      let closest = 0;
      let closestDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < parsedPoints.length; i++) {
        const dist = Math.abs(parsedPoints[i]!.x - mouseX);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }
      setHoverIdx(closest);
    },
    [dataPoints, parsedPoints],
  );
  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);
  const hoverPoint = hoverIdx !== null ? parsedPoints[hoverIdx] : null;
  const hoverData = hoverIdx !== null && dataPoints ? dataPoints[hoverIdx] : null;

  const tableRows: PremiumDiscountQuarterlyRow[] =
    quarterlyData && quarterlyData.length > 0
      ? quarterlyData
      : [
          {
            quarter: "Current Quarter",
            daysAtNav: "—",
            daysAtPremium: "—",
            daysAtDiscount: "—",
            pdRange: "—",
          },
        ];

  return (
    <section
      className="border-b border-[var(--etf-border)]"
      id="sec-premium"
      data-fund-section="twelvedataEnabled twelvedataSymbol"
    >
      <div className="flex items-center justify-between px-10 max-md:px-4 py-6 border-b border-[var(--etf-border)]">
        <div>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
            Premium / Discount
          </h2>
          <p className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em] mt-1 pl-[15px]">
            Quarterly Data &middot; Since Inception
          </p>
        </div>
      </div>

      {!hasRealData && (
        <div
          className="relative px-10 pb-7 max-md:px-4"
          aria-label="Premium / discount data unavailable"
        >
          <div
            className="w-full flex items-center justify-center border border-dashed border-[var(--etf-border)] rounded-sm font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.06em] opacity-60"
            style={{ aspectRatio: "800 / 160", minHeight: 120 }}
          >
            Premium / discount history will appear here once available.
          </div>
        </div>
      )}
      {hasRealData && (
        <div className="relative px-10 pb-7 max-md:px-4">
          <svg
            ref={svgRef}
            className="w-full h-auto block"
            viewBox="0 0 800 160"
            preserveAspectRatio="none"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: dataPoints ? "crosshair" : undefined }}
          >
            <defs>
              <linearGradient id="buffer-pd-pos" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(0,200,120,0.25)" />
                <stop offset="100%" stopColor="rgba(0,200,120,0)" />
              </linearGradient>
              <filter id="buffer-pd-glow">
                <feGaussianBlur result="blur" stdDeviation="2" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <line
              x1="0"
              x2="800"
              y1="80"
              y2="80"
              stroke="rgba(80,74,68,0.4)"
              strokeDasharray="4,4"
              strokeWidth="1"
            />
            <text x="0" y="76" fill="rgba(80,74,68,0.8)" fontFamily="Instrument Sans" fontSize="8">
              0.00%
            </text>
            <text x="0" y="18" fill="rgba(80,74,68,0.6)" fontFamily="Instrument Sans" fontSize="8">
              +0.80%
            </text>
            <text x="0" y="155" fill="rgba(80,74,68,0.6)" fontFamily="Instrument Sans" fontSize="8">
              &minus;0.80%
            </text>
            <path d={fillPath} fill="url(#buffer-pd-pos)" opacity="0.5" />
            <polyline
              fill="none"
              filter="url(#buffer-pd-glow)"
              points={points}
              stroke="var(--etf-orange)"
              strokeWidth="2"
            />
            {hoverIdx === null && (
              <circle
                cx={endDot.cx}
                cy={endDot.cy}
                r="3"
                fill="var(--etf-orange)"
                filter="url(#buffer-pd-glow)"
              />
            )}
            {hoverPoint && (
              <>
                <line
                  x1={hoverPoint.x}
                  x2={hoverPoint.x}
                  y1="0"
                  y2="160"
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
                <circle cx={hoverPoint.x} cy={hoverPoint.y} r="4.5" fill="var(--etf-orange)" />
                <circle
                  cx={hoverPoint.x}
                  cy={hoverPoint.y}
                  r="2.5"
                  fill="var(--etf-black2, #131313)"
                />
              </>
            )}
            {labels.map((label, i) => (
              <text
                key={`${label}-${i}`}
                x={BUFFER_PD_DATE_LABEL_X[i] ?? 20}
                y="155"
                fill="rgba(80,74,68,0.7)"
                fontFamily="Instrument Sans"
                fontSize="8"
              >
                {label}
              </text>
            ))}
          </svg>

          {hoverPoint && hoverData && (
            <div
              className="absolute pointer-events-none bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-sm px-3 py-2 min-w-[150px] z-10"
              style={{
                left: `${(hoverPoint.x / 800) * 100}%`,
                top: 0,
                transform: "translateX(-50%)",
              }}
            >
              <div className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.06em] mb-1.5">
                {fmtBufferPdDate(hoverData.date)}
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0"
                  style={{ background: "var(--etf-orange)" }}
                />
                P/D: {hoverData.pd >= 0 ? "+" : ""}
                {hoverData.pd.toFixed(2)}%
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0 opacity-60"
                  style={{ background: "var(--etf-white3, #9e9488)" }}
                />
                NAV: ${hoverData.nav.toFixed(2)}
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0 opacity-60"
                  style={{ background: "var(--etf-white3, #9e9488)" }}
                />
                Mkt: ${hoverData.mkt.toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto mb-7">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] px-10 py-3.5 text-left border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Quarter
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] px-10 py-3.5 text-right border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at NAV
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] px-10 py-3.5 text-right border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at Premium
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] px-10 py-3.5 text-right border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at Discount
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] px-10 py-3.5 text-right border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                P/D +/- 2.00%
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, i) => (
              <tr key={`${row.quarter}-${i}`}>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white)] px-10 py-[15px] text-left border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.quarter}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] px-10 py-[15px] text-right border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtNav}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] px-10 py-[15px] text-right border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtPremium}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] px-10 py-[15px] text-right border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtDiscount}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] px-10 py-[15px] text-right border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.pdRange}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-10 max-md:px-4 pt-3 pb-7 border-t border-[var(--etf-border2)] font-[family-name:var(--font-instrument)] text-[11.5px] text-[var(--etf-white)] tracking-[0.04em] italic leading-[1.5] m-0">
        The above table and line graph are provided to show the frequency at which the closing price
        of the Fund was at a premium (above) or discount (below) to the Fund&rsquo;s daily net asset
        value (&ldquo;NAV&rdquo;). The table and line graph represent past performance and cannot be
        used to predict future results. Shareholders may pay more than NAV when buying Fund shares
        and receive less than NAV when those shares are sold because shares are bought and sold at
        current. The Fund is newly organized and has no performance history. Performance data will
        be available after the Fund has completed a sufficient period of operations. Past
        performance does not guarantee future results.
      </p>
    </section>
  );
}
